/**
 * One ticket: the conversation, and the controls that move it.
 *
 * The internal-note switch is the thing to get right. A note marked internal is
 * excluded from the client's thread **in the query**, so it never reaches the
 * process that renders their page — but the person writing it only has this
 * switch to go on, so the editor changes colour and says plainly who will see
 * it. A client discovering that a private note was not is the failure this
 * whole feature is built around.
 *
 * Status is a select rather than a set of buttons: five states with one obvious
 * control beats five buttons where the current one has to be disabled.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EyeOff, Lock, Send } from 'lucide-react';
import {
  ApiError,
  Alert,
  Avatar,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  ErrorState,
  LoadingState,
  PageHeader,
  RecordSelect,
  Select,
  StatusChip,
  Switch,
  Textarea,
  api,
  cn,
  formatDateTime,
  formatRelative,
  toast,
} from '@connect/web-shared';

import { PRIORITY_TONES, TICKET_STATUS_LABELS, TICKET_STATUS_TONES } from '../../lib/tones';

interface Comment {
  public_id: string;
  body: string;
  internal: boolean;
  author_name: string;
  from_staff: boolean;
  created_at: string;
}

interface TicketDetail {
  public_id: string;
  code: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  client_public_id: string;
  client_name: string;
  project_name: string | null;
  assignee_name: string | null;
  raised_by: string | null;
  response_overdue: boolean;
  resolution_overdue: boolean;
  first_response_at: string | null;
  resolved_at: string | null;
  created_at: string;
  comments: Comment[];
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'waiting_on_client', label: 'Waiting on client' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function TicketDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);

  const ticket = useQuery({
    queryKey: ['support', 'tickets', publicId],
    queryFn: () => api.get<TicketDetail>(`/support/tickets/${publicId}`),
    enabled: Boolean(publicId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['support'] });
  };

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch(`/support/tickets/${publicId}`, patch),
    onSuccess: invalidate,
    onError: (error) => {
      toast.error(
        'Could not change that',
        error instanceof ApiError ? error.message : undefined,
      );
    },
  });

  const send = useMutation({
    mutationFn: (body: string) =>
      api.post(`/support/tickets/${publicId}/comments`, { body, internal }),
    onSuccess: () => {
      setReply('');
      invalidate();
      toast.success(internal ? 'Internal note added' : 'Reply sent to the client');
    },
    onError: (error) => {
      toast.error('Could not send', error instanceof ApiError ? error.message : undefined);
    },
  });

  if (ticket.isLoading) return <LoadingState label="Opening the ticket" />;
  if (ticket.isError) {
    const error = ticket.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Ticket not found' : undefined}
        message={error instanceof ApiError ? error.message : undefined}
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void ticket.refetch()}
      />
    );
  }

  const row = ticket.data;
  if (!row) return null;
  const closed = row.status === 'closed';

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.subject}
        description={`${row.code} · ${row.client_name}`}
        breadcrumbs={[{ label: 'Tickets', to: '/tickets' }, { label: row.code }]}
        actions={
          <>
            <StatusChip value={row.priority} tones={PRIORITY_TONES} />
            <StatusChip
              value={row.status}
              tones={TICKET_STATUS_TONES}
              labels={TICKET_STATUS_LABELS}
            />
          </>
        }
      />

      {row.response_overdue ? (
        <Alert tone="danger" title="Nobody has replied yet">
          The response target has passed. The clock stops when a **public** reply goes out — an
          internal note does not count, because the client has not heard anything.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader
              title={row.raised_by ? `Raised by ${row.raised_by}` : 'Raised'}
              description={formatDateTime(row.created_at)}
            />
            <CardBody>
              <p className="whitespace-pre-line text-sm leading-relaxed text-body">{row.body}</p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Conversation"
              description={
                row.comments.length === 0
                  ? 'Nothing yet.'
                  : `${row.comments.length} ${row.comments.length === 1 ? 'entry' : 'entries'}`
              }
            />
            <CardBody className="space-y-3">
              {row.comments.map((comment) => (
                <div key={comment.public_id} className="flex gap-2.5">
                  <Avatar name={comment.author_name} size="sm" />
                  <div
                    className={cn(
                      'min-w-0 flex-1 space-y-1 rounded-card border p-2.5',
                      comment.internal
                        ? // Visually distinct, and labelled. Colour alone would
                          // not survive a screenshot pasted into an email.
                          'border-warning/30 bg-warning-subtle'
                        : comment.from_staff
                          ? 'border-line bg-surface-2'
                          : 'border-accent/20 bg-accent-subtle',
                    )}
                  >
                    <p className="flex items-baseline justify-between gap-2 text-2xs">
                      <span className="flex items-center gap-1.5 font-medium text-body">
                        {comment.author_name}
                        {comment.internal ? (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-warning/15 px-1.5 py-0.5 text-warning">
                            <EyeOff className="size-2.5" />
                            Internal
                          </span>
                        ) : null}
                      </span>
                      <span
                        className="shrink-0 text-faint"
                        title={formatDateTime(comment.created_at)}
                      >
                        {formatRelative(comment.created_at)}
                      </span>
                    </p>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-body">
                      {comment.body}
                    </p>
                  </div>
                </div>
              ))}

              {closed ? (
                <p className="flex items-center gap-1.5 rounded-control bg-surface-2 px-3 py-2 text-xs text-muted">
                  <Lock className="size-3.5" />
                  This ticket is closed. Reopen it to add to the conversation.
                </p>
              ) : (
                <Can permission="support.ticket.edit">
                  <form
                    className="space-y-2 border-t border-line pt-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (reply.trim()) send.mutate(reply.trim());
                    }}
                  >
                    <Textarea
                      label={internal ? 'Internal note' : 'Reply to the client'}
                      rows={3}
                      value={reply}
                      onChange={(event) => setReply(event.target.value)}
                      className={internal ? 'border-warning/40 bg-warning-subtle' : undefined}
                      placeholder={
                        internal
                          ? 'Only staff will see this.'
                          : 'This goes to the client in their portal.'
                      }
                    />
                    <div className="flex items-center justify-between gap-3">
                      <Switch
                        label="Internal note"
                        checked={internal}
                        onChange={(event) => setInternal(event.target.checked)}
                      />
                      <Button
                        type="submit"
                        size="sm"
                        variant={internal ? 'secondary' : 'primary'}
                        loading={send.isPending}
                        disabled={!reply.trim()}
                      >
                        <Send className="size-3.5" />
                        {internal ? 'Add note' : 'Send reply'}
                      </Button>
                    </div>
                  </form>
                </Can>
              )}
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader title="Handling" />
          <CardBody className="space-y-4">
            <Can permission="support.ticket.edit">
              <Select
                label="Status"
                options={STATUS_OPTIONS}
                value={row.status}
                onChange={(event) => update.mutate({ status: event.target.value })}
                hint="Waiting on the client stops the response clock."
              />
              <Select
                label="Priority"
                options={PRIORITY_OPTIONS}
                value={row.priority}
                onChange={(event) => update.mutate({ priority: event.target.value })}
              />
            </Can>
            <Can permission="support.ticket.assign">
              <RecordSelect
                label="Assignee"
                endpoint="/hr/employees"
                value={null}
                valueLabel={row.assignee_name}
                onChange={(next) => update.mutate({ assignee_id: next })}
                toOption={(option) => ({
                  value: String(option.public_id),
                  label: String(option.full_name),
                  detail: String(option.job_title ?? ''),
                })}
                placeholder="Unassigned"
              />
            </Can>

            <DetailGrid className="sm:grid-cols-1">
              <DetailField label="Client">{row.client_name}</DetailField>
              <DetailField label="Project">{row.project_name ?? '—'}</DetailField>
              <DetailField label="First reply">
                {row.first_response_at ? formatDateTime(row.first_response_at) : 'Not yet'}
              </DetailField>
              <DetailField label="Resolved">
                {row.resolved_at ? formatDateTime(row.resolved_at) : 'Not yet'}
              </DetailField>
            </DetailGrid>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
